import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getAppEnv } from '../config/env';

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;

/**
 * Initialize Firebase from the host app's injected environment.
 *
 * Must run once at startup, after configureAppEnv() and before any service is
 * called. (No shared module touches auth/db at import time, so the live exports
 * below resolve correctly as long as init precedes the first call.)
 *
 * @param opts.authFactory  Web defaults to getAuth(); React Native must pass a
 *   factory using initializeAuth() + getReactNativePersistence(AsyncStorage),
 *   otherwise users are signed out on every app restart.
 */
function initFirebase(opts?: { authFactory?: (app: FirebaseApp) => Auth }): void {
  const env = getAppEnv();

  app = getApps().length === 0 ? initializeApp(env.firebase) : getApps()[0];
  auth = opts?.authFactory ? opts.authFactory(app) : getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);

  if (env.useEmulators) {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
  }
}

export { app, auth, db, storage, initFirebase };
